import React, { useEffect, useState } from 'react';
import { Zap, Clock, ArrowRight } from 'lucide-react';
import { publicApi } from '../api';
import { useFetch } from '../useFetch';

// Live countdown to a target ISO date; returns "2g 4h 12m" or null when expired
function useCountdown(iso) {
  const [remaining, setRemaining] = useState(() => Math.max(0, new Date(iso).getTime() - Date.now()));
  useEffect(() => {
    const t = setInterval(() => setRemaining(Math.max(0, new Date(iso).getTime() - Date.now())), 1000);
    return () => clearInterval(t);
  }, [iso]);
  if (remaining <= 0) return null;
  const s = Math.floor(remaining / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}g ${h}h ${m}m ${sec}s`;
  if (h > 0) return `${h}h ${m}m ${sec}s`;
  return `${m}m ${sec}s`;
}

function OfferCard({ offer }) {
  const cd = useCountdown(offer.expires_at);
  if (!cd) return null;
  const href = offer.link_url || (offer.activity_slug ? `#${offer.activity_slug}` : null);
  const Inner = (
    <div
      className="relative overflow-hidden rounded-2xl border border-[color:var(--yup-primary)]/40 bg-gradient-to-br from-black/80 via-black/60 to-[color:var(--yup-primary)]/10 backdrop-blur-md p-5 md:p-6 group hover:border-[color:var(--yup-primary)] transition-all"
      data-testid={`flash-offer-${offer.id}`}
    >
      {/* Corner promo badge */}
      {offer.promo_text && (
        <div
          className="absolute -top-1 -right-1 px-3 py-1.5 text-xs font-bold text-black tracking-widest shadow-lg"
          style={{
            background: 'linear-gradient(135deg, #f2e5b8, #BCAC8A, #8f7f5f)',
            clipPath: 'polygon(0 0, 100% 0, 100% 100%, 12px 100%, 0 calc(100% - 12px))'
          }}
        >
          {offer.promo_text}
        </div>
      )}
      <div className="flex items-center gap-2 text-[color:var(--yup-primary)] text-[10px] tracking-[0.3em] uppercase mb-3">
        <Zap size={12} /> Offerta flash
      </div>
      <h4 className="text-white text-lg md:text-xl font-bold leading-tight mb-2 group-hover:text-[color:var(--yup-primary)] transition">{offer.title}</h4>
      {offer.description && <p className="text-white/70 text-sm mb-4 line-clamp-2">{offer.description}</p>}
      <div className="flex items-center justify-between gap-3 mt-auto">
        <div className="flex items-center gap-1.5 text-[color:var(--yup-primary)] font-semibold text-sm font-mono">
          <Clock size={14} /> <span data-testid="offer-countdown">{cd}</span>
        </div>
        {href && <ArrowRight size={16} className="text-white/60 group-hover:translate-x-1 group-hover:text-[color:var(--yup-primary)] transition" />}
      </div>
    </div>
  );
  return href ? (
    <a href={href} target={offer.link_url ? '_blank' : '_self'} rel="noopener noreferrer sponsored">{Inner}</a>
  ) : Inner;
}

export default function FlashOffersSection({ citySlug }) {
  const { data: offers } = useFetch(
    `offers:${citySlug || 'all'}`,
    () => publicApi.flashOffers(citySlug || undefined),
    [citySlug]
  );
  if (!offers || offers.length === 0) return null;
  return (
    <section className="mt-8 fade-in-up" data-testid="flash-offers-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs tracking-[0.3em] uppercase text-[color:var(--yup-primary)] font-semibold flex items-center gap-2">
          <Zap size={14} /> Offerte flash
        </h3>
        <span className="text-[10px] text-white/40 tracking-widest uppercase">Solo per poco tempo</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {offers.map(o => <OfferCard key={o.id} offer={o} />)}
      </div>
    </section>
  );
}
